import { useState, useEffect } from 'react';
import { CloseButton } from './CloseButton';
import { hasConsent } from '../../services/consent/consentManager';

/**
 * Discord Invite Popup Component
 * Non-invasive popup that invites users to join the Discord community
 * Appears in bottom-right corner with dismiss and "don't show again" options
 */

const DISCORD_URL = 'https://discord.gg/NDHSpd7be5';
const STORAGE_KEY = 'discord-popup-dismissed';

interface DiscordInvitePopupProps {
  /** Delay in milliseconds before showing the popup (default: 3000ms) */
  delay?: number;
}

/**
 * Discord invite popup component
 * Shows a subtle invitation to join the Discord server
 * Respects user's "don't show again" preference via localStorage
 * Coordinates with the cookie banner so both are never visible simultaneously
 */
export function DiscordInvitePopup({ delay = 3000 }: DiscordInvitePopupProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
    // Check if user has dismissed permanently
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed === 'true') {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let scheduled = false;

    /**
     * Schedule the popup to appear after a given delay.
     * Uses a flag to prevent duplicate scheduling (Req 4.5).
     */
    const schedulePopup = (ms: number) => {
      if (scheduled) return;
      scheduled = true;
      timer = setTimeout(() => {
        setIsVisible(true);
      }, ms);
    };

    // If the cookie banner is currently visible (no consent decision yet),
    // wait for it to dismiss before scheduling the popup (Req 4.1, 4.2).
    if (!hasConsent()) {
      const handleBannerDismissed = () => {
        // Banner exit animation is 300ms; add 1000ms buffer after that,
        // then the normal popup delay (Req 4.3).
        schedulePopup(delay + 1000);
      };

      window.addEventListener('cookie-banner-dismissed', handleBannerDismissed);

      return () => {
        window.removeEventListener('cookie-banner-dismissed', handleBannerDismissed);
        if (timer) clearTimeout(timer);
      };
    }

    // Consent already exists — no banner shown, use normal delay (Req 4.4).
    schedulePopup(delay);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [delay]);

  const handleDismiss = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsAnimatingOut(false);
    }, 300);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    handleDismiss();
  };

  const handleJoinDiscord = () => {
    window.open(DISCORD_URL, '_blank', 'noopener,noreferrer');
    handleDismiss();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 999,
        maxWidth: '340px',
        animation: isAnimatingOut ? 'slideOut 0.3s ease-in' : 'slideIn 0.4s ease-out',
        pointerEvents: 'auto',
      }}
      className="discord-invite-popup"
      role="dialog"
      aria-labelledby="discord-invite-title"
      aria-describedby="discord-invite-description"
    >
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(88, 101, 242, 0.25) 0%, rgba(88, 101, 242, 0.15) 100%)',
          backdropFilter: 'blur(16px)',
          border: '3px solid rgba(88, 101, 242, 0.6)',
          borderRadius: '16px',
          padding: '1.25rem',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4), 0 0 16px rgba(88, 101, 242, 0.2)',
          fontFamily: 'var(--font-family)',
          position: 'relative',
          overflow: 'hidden',
          isolation: 'isolate',
        }}
      >
        {/* Animated background shimmer - Disabled to prevent performance issues */}
        {/* <div
          style={{
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'linear-gradient(45deg, transparent 40%, rgba(255, 255, 255, 0.04) 50%, transparent 60%)',
            animation: 'shimmer 5s infinite',
            pointerEvents: 'none',
          }}
        /> */}
        {/* Close button */}
        <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
          <CloseButton
            onClick={handleDismiss}
            ariaLabel="Dismiss Discord invite"
            size="small"
            variant="minimal"
          />
        </div>

        {/* Discord icon and title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', position: 'relative', zIndex: 1 }}>
          <div
            style={{
              fontSize: '2.5rem',
              filter: 'drop-shadow(0 4px 8px rgba(88, 101, 242, 0.8))',
            }}
          >
            💬
          </div>
          <h3
            id="discord-invite-title"
            style={{
              margin: 0,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: '#ffffff',
              letterSpacing: '0.5px',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)',
            }}
          >
            Join Our Discord!
          </h3>
        </div>

        {/* Description */}
        <p
          id="discord-invite-description"
          style={{
            margin: '0 0 1rem 0',
            fontSize: '0.9rem',
            lineHeight: '1.6',
            color: '#ffffff',
            textShadow: '0 1px 4px rgba(0, 0, 0, 0.8)',
          }}
        >
          Connect with other 8-bit music creators, share your compositions, and get help from the community!
        </p>

        {/* Competition announcement */}
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 165, 0, 0.15) 100%)',
            border: '2px solid rgba(255, 215, 0, 0.5)',
            borderRadius: '8px',
            padding: '0.75rem',
            marginBottom: '1rem',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: '0.85rem',
              lineHeight: '1.5',
              color: '#ffffff',
              textShadow: '0 1px 4px rgba(0, 0, 0, 0.8)',
            }}
          >
            🏆 <strong>Competition coming soon!</strong> Join now to be alerted when it begins and win free credits!
          </p>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button
            onClick={handleJoinDiscord}
            style={{
              background: 'linear-gradient(135deg, #5865F2 0%, #4752C4 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '0.875rem 1rem',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 4px 12px rgba(88, 101, 242, 0.5)',
              minHeight: '44px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(88, 101, 242, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(88, 101, 242, 0.5)';
            }}
          >
            Join Discord Server
          </button>

          <button
            onClick={handleDontShowAgain}
            style={{
              background: 'none',
              color: '#ffffff',
              border: 'none',
              padding: '0.5rem',
              fontSize: '0.8rem',
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
              opacity: 0.8,
              transition: 'opacity 0.2s',
              textShadow: '0 1px 4px rgba(0, 0, 0, 0.8)',
              minHeight: '44px',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.8')}
          >
            Don't show again
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateY(100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideOut {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(100px);
            opacity: 0;
          }
        }

        /* Mobile: Center the popup */
        @media (max-width: 768px) {
          .discord-invite-popup {
            left: 20px !important;
            right: 20px !important;
            margin: 0 auto !important;
          }
        }
      `}</style>
    </div>
  );
}
